/**
 * The map picker: choose which map to drive.
 *
 * The list comes from `maps/index.json`, which the build generates by reading
 * `public/maps/` (vite.config.ts). Nothing here knows the names of any maps —
 * drop ten files into that folder and ten entries appear. A hard-coded list
 * would be a second description of the same thing, and the two would disagree
 * the first time someone added a map in a hurry.
 *
 * Selecting a map reloads the page with `?map=<id>`, keeping every other
 * query parameter: a person comparing maps has usually set a seed, a speed
 * profile or an assist model, and losing those on every switch would make the
 * comparison meaningless.
 */

import type { MapSummary } from "../sim/map/describe";


/** The built-in district. Selecting it drops `?map=` and plays the code path,
 * which is the instrument the research runs on (ADR-024). */
export const BUILTIN_MAP_ID = "district_v2";

/** One row of the index. Shared with the build, so the two cannot drift. */
export type MapIndexEntry = MapSummary;

/**
 * Fetch the index. Returns an empty list rather than throwing: a missing
 * index must leave the game playable, with the picker saying it found nothing.
 */
export async function fetchMapIndex(): Promise<MapIndexEntry[]> {
  try {
    const res = await fetch(`${import.meta.env.BASE_URL}maps/index.json`, { credentials: "omit" });
    if (!res.ok) return [];
    const raw: unknown = await res.json();
    return Array.isArray(raw) ? raw.filter(isEntry) : [];
  } catch {
    return [];
  }
}

function isEntry(v: unknown): v is MapIndexEntry {
  if (typeof v !== "object" || v === null) return false;
  const e = v as Record<string, unknown>;
  return typeof e["id"] === "string" && typeof e["name"] === "string";
}

/**
 * The URL that plays `id`, preserving the rest of the query.
 *
 * Exported so it can be unit-tested without a browser: getting this wrong
 * silently resets somebody's seed, which is the kind of bug that only shows
 * up as "the numbers moved and I don't know why".
 */
export function mapUrl(search: string, id: string): string {
  const q = new URLSearchParams(search);
  if (id === BUILTIN_MAP_ID) q.delete("map");
  else q.set("map", id);
  const s = q.toString();
  return s ? `?${s}` : window.location.pathname;
}

/**
 * The maps a fresh visitor is sent to, in order.
 *
 * The built-in district is excluded: it is the pinned research instrument
 * (ADR-024), and dropping an anonymous visitor into it means their run lands
 * in the same map as the study data. The scenario maps are what the sequence
 * is for.
 */
export function playableOrder(
  entries: readonly MapIndexEntry[],
  includePhotographic = false,
): MapIndexEntry[] {
  return entries.filter(
    (e) => e.id !== BUILTIN_MAP_ID && (includePhotographic || !e.photographic),
  );
}

/**
 * A random map for someone arriving with no preference.
 *
 * Takes its randomness as an argument rather than calling Math.random itself,
 * so the choice can be tested and so a caller that wants a reproducible
 * session can supply its own source.
 */
export function randomMapId(
  entries: readonly MapIndexEntry[],
  rand: () => number = Math.random,
  exclude?: string,
  includePhotographic = false,
): string | null {
  const pool = playableOrder(entries, includePhotographic).filter((e) => e.id !== exclude);
  // With one map, "another random one" can only be the one already playing;
  // returning null lets the caller leave it alone rather than pretend.
  if (pool.length === 0) return null;
  return pool[Math.min(pool.length - 1, Math.floor(rand() * pool.length))]!.id;
}

/**
 * The next map in order, wrapping at the end.
 *
 * Sequential rather than random so finishing one map leads somewhere — a
 * random jump after every run makes the set feel like a bag rather than a
 * progression, and these maps are ordered easy to hard on purpose.
 */
export function nextMapId(
  entries: readonly MapIndexEntry[],
  currentId: string,
  includePhotographic = false,
): string | null {
  const order = playableOrder(entries, includePhotographic);
  if (order.length === 0) return null;
  const i = order.findIndex((e) => e.id === currentId);
  // Not in the sequence (the district, or a map that has since been removed):
  // start at the beginning rather than nowhere.
  return order[i < 0 ? 0 : (i + 1) % order.length]!.id;
}

/**
 * Marker that the next page load came from the welcome card, so the card is
 * shown again after a reroll.
 *
 * sessionStorage rather than a query parameter: rerolling lands on an ordinary
 * `?map=<id>` URL, which is the URL a player might copy or share, and a
 * `&welcome=1` riding along in it would put a card in front of whoever they
 * sent it to. The signal belongs to this tab, not to the address.
 */
const WELCOME_KEY = "streetsense_welcome";

export function armWelcome(): void {
  try {
    sessionStorage.setItem(WELCOME_KEY, "1");
  } catch {
    /* private mode: the card just does not follow the reroll */
  }
}

/** Read and clear the marker — it applies to exactly one load. */
export function takeWelcome(): boolean {
  try {
    const armed = sessionStorage.getItem(WELCOME_KEY) === "1";
    sessionStorage.removeItem(WELCOME_KEY);
    return armed;
  } catch {
    return false;
  }
}

/** Which entry is playing now, given `?map=` (absent = the built-in district). */
export function currentMapId(search: string): string {
  return new URLSearchParams(search).get("map") ?? BUILTIN_MAP_ID;
}

/**
 * Whether the real-street reconstructions are switched on for this visit.
 *
 * They are off unless asked for, because each one is tens of megabytes of
 * Gaussians and the built scenarios open in a moment. Somebody arriving to try
 * the simulator should not be made to wait for a photographic map they did not
 * ask for; somebody who came for the 3D street adds `?real=1`, or presses the
 * toggle in the picker, and gets them.
 *
 * An explicit `?map=<id>` is always honoured, flag or not: a link someone was
 * sent has to open the map it names.
 */
export function realMapsEnabled(search: string): boolean {
  return new URLSearchParams(search).get("real") === "1";
}

/** The URL that turns the real-street maps on or off, keeping the rest of the query. */
export function realMapsUrl(search: string, on: boolean): string {
  const q = new URLSearchParams(search);
  if (on) q.set("real", "1");
  else {
    q.delete("real");
    q.delete("map");          // leaving a photographic map selected would contradict the switch
  }
  const s = q.toString();
  return s ? `?${s}` : window.location.pathname;
}

/** Render the list into `host`. `onPick` receives the chosen map id. */
export function renderMapList(
  host: HTMLElement,
  entries: readonly MapIndexEntry[],
  currentId: string,
  onPick: (id: string) => void,
  includePhotographic = false,
): void {
  host.textContent = "";
  const listed = entries.filter(
    (e) => includePhotographic || !e.photographic || e.id === currentId,
  );
  if (listed.length === 0) {
    const empty = document.createElement("div");
    empty.className = "map-empty";
    empty.textContent = "No maps found in public/maps/.";
    host.appendChild(empty);
    return;
  }

  // Maps that are a real street come first. The index is ordered by id, which
  // is fine for a set of built scenarios and wrong the moment reconstructions
  // join them: somebody opening the site to see the 3D street should not have
  // to scroll past twenty hand-built scenarios to find one.
  const ordered = [...listed].sort((a, b2) => Number(!!b2.photographic) - Number(!!a.photographic));
  for (const e of ordered) {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "map-item";
    b.dataset["mapId"] = e.id;
    const current = e.id === currentId;
    if (current) b.setAttribute("aria-current", "true");

    const title = document.createElement("div");
    title.className = "map-item-title";
    title.textContent = e.name;
    if (current) {
      const now = document.createElement("span");
      now.className = "map-badge map-badge-now";
      now.textContent = "playing";
      title.appendChild(now);
    }
    // Exploration maps can never be pooled with study data, and somebody
    // choosing a map for a session needs to see that before they drive it,
    // not afterwards in the telemetry.
    if (e.photographic) {
      const real = document.createElement("span");
      real.className = "map-badge map-badge-photographic";
      real.textContent = "real street";
      title.appendChild(real);
    }
    const kind = document.createElement("span");
    kind.className = `map-badge ${e.research ? "map-badge-research" : "map-badge-explore"}`;
    kind.textContent = e.research ? "research" : "exploration";
    title.appendChild(kind);
    b.appendChild(title);

    if (e.description) {
      const d = document.createElement("div");
      d.className = "map-item-desc";
      d.textContent = e.description;
      b.appendChild(d);
    }

    const meta = document.createElement("div");
    meta.className = "map-item-meta";
    const bits = [`${e.roads} road${e.roads === 1 ? "" : "s"}`];
    if (e.encounters > 0) bits.push(`${e.encounters} encounter${e.encounters === 1 ? "" : "s"}`);
    if (e.author) bits.push(e.author);
    meta.textContent = bits.join(" · ");
    b.appendChild(meta);

    b.addEventListener("click", () => onPick(e.id));
    host.appendChild(b);
  }
}
